<template>
  <modal name="request-history-modal" :height="'auto'" :width="'550'" :adaptive="true" :scrollable="true">
    <div class="flex items-center justify-between p-4 border-b">
      <h4 class="text-base font-semibold">Talep İşlem Geçmişi</h4>
      <button @click="$modal.hide('request-history-modal')">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="p-4">
      <!-- Silinmiş talep uyarısı -->
      <div v-if="isDeleted && !loading" class="mb-3 p-2 bg-red-50 border border-red-300 rounded text-xs text-red-800">
        <i class="fas fa-trash mr-1"></i>
        Bu talep silinmiştir. Aşağıda silinmeden önceki işlem geçmişi gösteriliyor.
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center items-center h-32">
        <i class="fas fa-spinner fa-spin text-2xl text-gray-400"></i>
      </div>

      <div v-else-if="visibleEvents.length === 0" class="text-center text-gray-400 py-8">
        Henüz işlem geçmişi bulunmuyor.
      </div>

      <!-- Timeline -->
      <div v-else class="relative pl-6">
        <!-- Dikey çizgi -->
        <div class="absolute left-2 top-2 bottom-2 w-0.5 bg-gray-300"></div>

        <div v-for="(event, idx) in visibleEvents" :key="idx" class="relative mb-5 last:mb-0">
          <!-- Nokta -->
          <div class="absolute -left-6 top-1 w-4 h-4 rounded-full border-2 border-white" :style="{ backgroundColor: stepColor(event.step) }"></div>

          <!-- İçerik -->
          <div class="bg-gray-50 rounded p-3">
            <div class="flex items-center justify-between mb-1">
              <span class="text-sm font-semibold" :style="{ color: stepColor(event.step) }">
                <i :class="stepIcon(event.step)" class="mr-1"></i>{{ event.label }}
              </span>
              <span class="text-xxs text-gray-500">{{ formatDate(event.date) }}</span>
            </div>

            <!-- Yakıt Alındı adımı için kalem kalem alınan litreler -->
            <div v-if="event.step === 'fueled'" class="mt-1 mb-1">
              <div v-if="getFueledItems(event).length > 0" class="flex flex-wrap items-center gap-1">
                <span v-for="(it, idx) in getFueledItems(event)" :key="idx" class="px-2 py-0.5 text-xxs rounded bg-purple-100 text-purple-700">
                  <i class="fas fa-oil-can mr-1"></i>{{ it.fuelType }}:
                  <span class="font-semibold">{{ it.liters }} Lt</span>
                </span>
              </div>
              <div v-else class="text-xxs text-gray-400 italic">Kalem detayı bulunmuyor</div>
            </div>
            <div v-if="event.actor" class="flex items-center mt-1">
              <span
                class="px-1.5 py-0.5 text-white text-xxs rounded mr-2"
                :style="{ backgroundColor: actorRole(event.step).color }"
              >
                <i :class="actorRole(event.step).icon" class="mr-1"></i>{{ actorRole(event.step).label }}
              </span>
              <span class="text-xs text-gray-700 font-medium">{{ event.actor }}</span>
            </div>
            <div v-if="event.reason" class="text-xs mt-1 p-2 rounded bg-red-50 text-red-700">
              <i class="fas fa-info-circle mr-1"></i><span class="font-medium">Red Nedeni:</span> {{ event.reason }}
            </div>
            <div v-if="event.note" class="text-xs text-gray-500 mt-1 italic">{{ event.note }}</div>

            <!-- Fiş Yüklendi adımı için buton -->
            <button v-if="event.step === 'receiptUploaded' && receiptFileUrl"
              class="mt-2 px-3 py-1 text-white text-xxs rounded"
              style="background-color: #f97316;"
              @click="$emit('view-receipt', receiptFileUrl)">
              <i class="fas fa-receipt mr-1"></i>Fişi Gör
            </button>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import moment from "moment";
import "moment/locale/tr";

export default {
  name: "request-history-modal",
  props: {
    events: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    receiptFileUrl: { type: String, default: "" },
    isDeleted: { type: Boolean, default: false },
    requestItems: { type: Array, default: () => [] },
  },
  computed: {
    visibleEvents() {
      return this.events
        .filter((e) => e.date || e.reason)
        .slice()
        .reverse();
    },
  },
  methods: {
    stepColor(step) {
      const map = {
        created: "#3b82f6",
        approved: "#22c55e",
        rejected: "#ef4444",
        directedToStation: "#0ea5e9",
        fueled: "#a855f7",
        receiptUploaded: "#f97316",
        completed: "#16a34a",
        deleted: "#b91c1c",
      };
      return map[step] || "#6b7280";
    },
    stepIcon(step) {
      const map = {
        created: "fas fa-file-alt",
        approved: "fas fa-check",
        rejected: "fas fa-times",
        directedToStation: "fas fa-gas-pump",
        fueled: "fas fa-oil-can",
        receiptUploaded: "fas fa-receipt",
        completed: "fas fa-flag-checkered",
        deleted: "fas fa-trash",
      };
      return map[step] || "fas fa-circle";
    },
    actorRole(step) {
      const operatorSteps = ["approved", "rejected", "directedToStation", "completed", "deleted"];
      if (operatorSteps.includes(step)) {
        return { label: "Operatör", color: "#2563eb", icon: "fas fa-user-tie" };
      }
      // created, fueled, receiptUploaded → sürücü
      return { label: "Sürücü", color: "#0d9488", icon: "fas fa-id-badge" };
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("LLL");
    },
    formatMoney(val) {
      if (val == null) return "0,00";
      return Number(val).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    getFueledItems(event) {
      // Önce event'in kendi items'ını dene
      if (event.items && event.items.length > 0) {
        return event.items.map(it => ({
          fuelType: it.fuelType,
          liters: it.actualLiters != null ? it.actualLiters : (it.liters != null ? it.liters : 0),
        }));
      }
      // Yoksa parent talebin items'ından actualLiters dolu olanları al
      if (this.requestItems && this.requestItems.length > 0) {
        return this.requestItems
          .filter(it => it.actualLiters != null)
          .map(it => ({
            fuelType: it.fuelType,
            liters: it.actualLiters,
          }));
      }
      return [];
    },
  },
};
</script>
